import 'components/ordering/quotation/components/request/lines.js'
import { html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { consume } from '@lit/context'
import {
  getOpenQuotationContext,
  NotFetchedYet,
  openQuotationContext,
  updateQuotationInternalWithoutRequestContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { listenForQuotationUpdates } from 'components/ordering/quotation/components/quotation-update-event.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { redirectToPath } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'


const translations = {
  header: () => t('header', { ns: 'csc-quotation-request-slide-in', default: 'Request Quote' }),
  continue_shopping: () => t('continue_shopping', { ns: 'csc-quotation-request-slide-in', default: 'Continue shopping' }),
  review: () => t('review', { ns: 'csc-quotation-request-slide-in', default: 'Review' })
}

@customElement('csc-quotation-request-slide-in')
export class CSCQuotationRequestSlideIn extends LitElement {
  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @consume({ context: openQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @consume({ context: getOpenQuotationContext })
  private getOpenQuotation!: () => Promise<void>

  @consume({ context: updateQuotationInternalWithoutRequestContext })
  private updateQuotationInternalWithoutRequest!: (quotation: QuotationDTO) => Promise<void>

  @property({ type: String, attribute: 'target-id' })
  targetId: string = 'default'

  @query('cse-slide-in')
  private _slideIn!: CSESlideIn | null | undefined

  async createSlideIn(): Promise<CSCQuotationRequestSlideIn | null | undefined> {
    const existingSlideIn = document.querySelector('csc-quotation-request-slide-in') as CSCQuotationRequestSlideIn | null
    if (existingSlideIn) return existingSlideIn

    const newSlideInElement = document.createElement('csc-quotation-request-slide-in')
    document.body.appendChild(newSlideInElement)

    await customElements.whenDefined('csc-quotation-request-slide-in').catch(() => {
    })
    if ('updateComplete' in newSlideInElement && (newSlideInElement as any).updateComplete instanceof Promise) {
      await (newSlideInElement as any).updateComplete
    } else {
      this.requestUpdate()
    }

    return newSlideInElement as CSCQuotationRequestSlideIn
  }

  open() {
    if (this._slideIn) {
      this._slideIn.open().then(() => nothing)
    }
  }

  continueShopping() {
    this._slideIn?.close()
  }

  async review() {
    if (isQuotationDTO(this.quotation) && this.quotation.status === 'open') {
      await redirectToPath(this.windowLocation, `account/quotation/${this.quotation.id}/`)
      return
    }
  }

  override async connectedCallback() {
    super.connectedCallback()

    listenForQuotationUpdates(async (quotation) => {
      if (!isQuotationDTO(quotation)) return
      await this.updateQuotationInternalWithoutRequest(quotation)
    })
  }

  override async updated() {
    if (!isQuotationDTO(this.quotation)) {
      await this.getOpenQuotation?.()
    }
  }

  override render(): TemplateResult {
    return html`
      <cse-slide-in size="large" id="${this.targetId}" placement="right">
        <div slot="header">
          ${translations.header()}
        </div>

        <csc-quotation-lines></csc-quotation-lines>

        <cse-button slot="footer" fit="fill" @click=${this.continueShopping}>
          ${translations.continue_shopping()}
        </cse-button>
        <cse-button slot="footer" fit="fill" variant="primary" @click=${this.review}>
          ${translations.review()}
        </cse-button>
      </cse-slide-in>
    `
  }
}
